/**
 * GoogleSignInButton Component
 * 
 * Reusable Google Sign-In button component that handles OAuth flow
 * and integrates with the backend authentication system.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from './Toast';
import { 
  initializeGoogleIdentity, 
  handleGoogleCredentialResponse, 
  isGoogleAuthConfigured 
} from '../services/googleAuth';
import { getApiErrorMessage } from '../services/api';

const GoogleSignInButton = ({ 
  onSuccess, 
  onError, 
  disabled = false, 
  theme = 'light',
  className = '' 
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const buttonRef = useRef(null);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    if (!isGoogleAuthConfigured()) {
      console.warn('Google OAuth not properly configured');
      return;
    }

    const initGoogle = async () => {
      try {
        await initializeGoogleIdentity();
        setIsGoogleReady(true);
      } catch (error) {
        console.error('Failed to initialize Google Identity:', error);
        showError('Failed to load Google Sign-In');
      }
    };

    initGoogle();
  }, [showError]);

  useEffect(() => {
    if (isGoogleReady && googleButtonRef.current && window.google) {
      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse
      });

      // Get the actual width of the parent container
      const containerWidth = googleButtonRef.current.parentElement?.offsetWidth || 
                            googleButtonRef.current.offsetWidth || 
                            400; // fallback width

      // Render the button
      window.google.accounts.id.renderButton(
        googleButtonRef.current,
        {
          theme: theme === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          width: containerWidth,
          logo_alignment: 'left',
          text: 'signin_with'
        }
      );
    }
  }, [isGoogleReady, theme]);

  const handleCredentialResponse = async (response) => {
    setIsLoading(true);
    
    try {
      const result = await handleGoogleCredentialResponse(response);
      
      if (result.token) {
        showSuccess('Successfully signed in with Google!');
        
        if (onSuccess) {
          onSuccess(result);
        } else {
          // Default behavior - redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      const errorMessage = getApiErrorMessage(error);
      showError(errorMessage || 'Failed to sign in with Google');
      
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFallbackClick = () => {
    if (!isGoogleReady) {
      showError('Google Sign-In is not ready. Please try again.');
      return;
    }

    // Trigger Google One Tap or popup
    window.google.accounts.id.prompt();
  };

  if (!isGoogleAuthConfigured()) {
    return (
      <div className={`text-center p-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
        <p className="text-sm">Google Sign-In not configured</p>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Google's rendered button */}
      <div 
        ref={googleButtonRef}
        className={`w-full ${disabled || isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        style={{ 
          minHeight: '44px',
          width: '100% !important'
        }}
      />
      
      {/* Fallback button if Google button doesn't load */}
      {!isGoogleReady && (
        <button
          ref={buttonRef}
          type="button"
          onClick={handleFallbackClick}
          disabled={disabled || isLoading}
          className={`w-full inline-flex justify-center items-center py-2 px-4 border ${
            theme === 'dark' 
              ? 'border-gray-700 bg-gray-800 text-white hover:bg-gray-700' 
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          } rounded-md shadow-sm text-sm font-medium transition-all duration-200 transform hover:scale-105 ${
            disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {isLoading ? (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          {isLoading ? 'Signing in...' : 'Continue with Google'}
        </button>
      )}
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center rounded-md">
          <svg className="animate-spin h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
    </div>
  );
};

export default GoogleSignInButton;
